import AutoComplete from 'components/Common/AutoComplete';
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import SectionHeader from 'components/IAM/Admin/SectionHeader';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore,
  CloudUpload as ExportIcon,
  Info as InfoIcon,
  Assignment as JobIcon,
} from '@mui/icons-material';
import { FlowBuilderContext } from 'pages/Flow/FlowBuilder/FlowBuilderContextProvider';
import { HButton, HSpinner } from 'BaseComponents';
import { exportParameterMap, exportParams } from 'components/DataOut/constants';
import { habuColors } from 'Theme';
import { useCreateFlowNodeExportJob, useGetFlowNodeExportJobs, useUpdateFlowNodeExportJobStatus } from 'hooks/flowExportAPI';
import { useGetPartnerProvisions } from 'hooks/activationAPI';


const FlowNodeExportConfiguration = ({ node }) => {
  const { crId, flowId } = useContext(FlowBuilderContext);

  // State management
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [exportParameters, setExportParameters] = useState([]);
  const [isConfigurationValid, setIsConfigurationValid] = useState(false);
  const [updatingJobs, setUpdatingJobs] = useState(new Set());
  const [exportJobs, setExportJobs] = useState([]);

  // API hooks
  const {
    data: partnerProvisions,
    isLoading: isLoadingProvisions,
    error: provisionsError,
  } = useGetPartnerProvisions(crId);

  const {
    mutateAsync: createExportJob,
    isLoading: isCreatingJob,
    error: createJobError,
  } = useCreateFlowNodeExportJob();

  // Export jobs list API hooks
  const {
    data: exportJobsData,
    isLoading: isLoadingJobs,
    isSuccess: isSuccessJobs,
    error: jobsError,
    refetch: refetchJobs,
  } = useGetFlowNodeExportJobs({ crId, flowId, nodeId: node.id });

  const {
    mutateAsync: updateJobStatus,
  } = useUpdateFlowNodeExportJobStatus();

  // Filter out LR-Activation destinations because they are only used in Node Segmentation
  const filteredPartnerProvisions = useMemo(() => {
    if (!partnerProvisions) return [];
    return partnerProvisions.filter(
      provision => provision.partnerAccount?.partner?.name !== 'LR-Activation',
    );
  }, [partnerProvisions]);

  // Initialize export parameters when destination changes
  useEffect(() => {
    if (selectedDestination) {
      const paramList = exportParameterMap[selectedDestination.partnerAccount.partner?.name] || [];
      const newParameters = paramList.map(paramName => ({
        name: paramName,
        value: paramName === exportParams.CSV_EXPORT ? 'false' : '',
        required: !paramName.includes('Optional'),
      }));
      setExportParameters(newParameters);
    }
    else {
      setExportParameters([]);
    }
  }, [selectedDestination]);

  // Validate configuration
  useEffect(() => {
    const hasDestination = !!selectedDestination;
    const hasRequiredParams = exportParameters
      .filter(p => p.required)
      .every(p => p.value.trim() !== '');
    setIsConfigurationValid(hasDestination && hasRequiredParams);
  }, [selectedDestination, exportParameters]);

  // Update export jobs local state with API data (on API success)
  useEffect(() => {
    if (!isLoadingJobs && isSuccessJobs) {
      setExportJobs(exportJobsData?.jobs ?? []);
    }
  }, [exportJobsData, isLoadingJobs, isSuccessJobs]);

  const handleDestinationChange = (event, newValue) => {
    setSelectedDestination(newValue);
  };

  const handleParameterChange = (paramName, newValue) => {
    setExportParameters(prev => prev.map(param => (param.name === paramName
      ? { ...param, value: newValue }
      : param),
    ),
    );
  };

  const handleCreateExportJob = async () => {
    try {
      await createExportJob({
        cleanRoomId: crId,
        flowId,
        nodeId: node.id,
        partnerAccountIDs: [selectedDestination.partnerAccount.ID],
        exportParameters: exportParameters.map(param => ({
          name: param.name,
          value: param.value,
        })),
      });
      // Clear form after successful creation
      setSelectedDestination(null);
      setExportParameters([]);
      // Refetch export jobs to show the newly created job
      await refetchJobs();
    }
    catch (error) {
      // Error handling is managed by the hook - no additional logging needed
    }
  };

  const handleStatusToggle = async (jobId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';

    // Add the job to update from "updating" state (before fetching)
    setUpdatingJobs(prev => new Set([...prev, jobId]));

    try {
      // Update the job to new status
      await updateJobStatus({
        cleanRoomId: crId,
        flowId,
        nodeId: node.id,
        jobId,
        status: newStatus,
      });

      // On update job status success, update job changed in local state
      setExportJobs(prevExportJobs => (
        prevExportJobs.map((job) => (
          (job.ID === jobId) ? ({ ...job, status: newStatus }) : job
        ))
      ));
    }
    catch (error) {
      // Error is handled by the hook - no additional logging needed
    }
    finally {
      // Remove the updated job from "updating" state (after fetching)
      setUpdatingJobs(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return habuColors.signal.success[20];
      case 'PAUSED':
        return habuColors.signal.warning[20];
      default:
        return habuColors.neutral[30];
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'PAUSED':
        return 'Paused';
      default:
        return 'Unknown';
    }
  };

  const renderParameterField = (param) => {
    const { name, value, required } = param;

    // Handle different parameter types
    switch (name) {
      case exportParams.CSV_EXPORT:
        return (
          <Grid item xs={12} key={name}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={value === 'true'}
                  onChange={(e) => handleParameterChange(name, e.target.checked ? 'true' : 'false')}
                />
              )}
              label='Enable CSV Export'
            />
          </Grid>
        );

      case 'Dataset Type':
        return (
          <Grid item xs={12} md={6} key={name}>
            <SectionHeader subtitle='Dataset Type' />
            <Select
              value={value}
              onChange={(e) => handleParameterChange(name, e.target.value)}
              fullWidth
              size='small'
              required={required}
            >
              <MenuItem value='DIMENSION'>Dimension</MenuItem>
              <MenuItem value='FACT'>Fact</MenuItem>
            </Select>
          </Grid>
        );

      case exportParams.GCS_EXPORT_PATH:
        return (
          <Grid item xs={12} md={6} key={name}>
            <SectionHeader subtitle='GCS Export Path' />
            <TextField
              fullWidth
              size='small'
              value={value}
              onChange={(e) => handleParameterChange(name, e.target.value)}
              placeholder='bucket/path/to/exports'
              required={required}
              helperText={value ? `Final path: gs://${value}/*` : 'Enter the GCS bucket path'}
            />
          </Grid>
        );

      case exportParams.S3_EXPORT_PATH:
        return (
          <Grid item xs={12} md={6} key={name}>
            <SectionHeader subtitle='S3 Export Path' />
            <TextField
              fullWidth
              size='small'
              value={value}
              onChange={(e) => handleParameterChange(name, e.target.value)}
              placeholder='bucket/path/to/exports'
              required={required}
              helperText={value ? `Final path: s3a://${value}/*` : 'Enter the S3 bucket path'}
            />
          </Grid>
        );

      case exportParams.AZURE_STORAGE_ACCOUNT:
      case exportParams.AZURE_STORAGE_CONTAINER:
      case exportParams.AZURE_EXPORT_PATH:
        return (
          <Grid item xs={12} md={6} key={name}>
            <SectionHeader subtitle={name} />
            <TextField
              fullWidth
              size='small'
              value={value}
              onChange={(e) => handleParameterChange(name, e.target.value)}
              required={required}
              placeholder={`Enter ${name.toLowerCase()}`}
            />
          </Grid>
        );

      default:
        return (
          <Grid item xs={12} md={6} key={name}>
            <SectionHeader subtitle={name} />
            <TextField
              fullWidth
              size='small'
              value={value}
              onChange={(e) => handleParameterChange(name, e.target.value)}
              required={required}
              placeholder={`Enter ${name.toLowerCase()}`}
            />
          </Grid>
        );
    }
  };



  // Loading state
  if (isLoadingProvisions) {
    return (
      <Accordion defaultExpanded disableGutters elevation={0} style={{ width: '100%' }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{ background: theme => theme.palette.secondary.light }}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <ExportIcon />
            <Typography variant='subtitle1'>Export Configuration</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Skeleton height={200} />
        </AccordionDetails>
      </Accordion>
    );
  }

  // Error state
  if (provisionsError) {
    return (
      <Accordion defaultExpanded disableGutters elevation={0} style={{ width: '100%' }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{ background: theme => theme.palette.secondary.light }}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <ExportIcon />
            <Typography variant='subtitle1'>Export Configuration</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity='error'>
            Failed to load export destinations. Please try again.
          </Alert>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Export Configuration */}
      <Accordion defaultExpanded disableGutters elevation={0} style={{ width: '100%' }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{ background: theme => theme.palette.secondary.light }}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <ExportIcon />
            <Typography variant='subtitle1'>Export Configuration</Typography>
            <Tooltip title="Configure export jobs to send this node&apos;s results to external destinations">
              <InfoIcon fontSize='small' sx={{ color: habuColors.neutral[60] }} />
            </Tooltip>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            {/* Header */}
            <Typography variant='body3' color='text.secondary'>
              Export this node&apos;s results to external destinations. Select destinations and
              configure the required parameters for each export type.
            </Typography>

            {/* Destination Selection */}
            <Box>
              <SectionHeader subtitle='Select Export Destination' />
              <AutoComplete
                options={filteredPartnerProvisions}
                getOptionLabel={(option) => `${option.partnerAccount.partner?.displayName} - ${option.partnerAccount.name}`}
                value={selectedDestination}
                onChange={handleDestinationChange}
                textFieldProps={{
                  placeholder: 'Choose export destination...',
                  helperText: selectedDestination ? `Selected: ${selectedDestination.partnerAccount.partner?.displayName}` : 'Select one destination for export',
                  size: 'small',
                }}
              />
            </Box>

            {/* Export Parameters */}
            {exportParameters.length > 0 && (
              <Box>
                <SectionHeader subtitle='Export Parameters' />
                <Grid container spacing={2}>
                  {exportParameters.map(renderParameterField)}
                </Grid>
              </Box>
            )}

            {/* Create Export Job Button */}
            {selectedDestination && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <HButton
                  variant='contained'
                  color='primary'
                  onClick={handleCreateExportJob}
                  disabled={!isConfigurationValid || isCreatingJob}
                  startIcon={<AddIcon />}
                  endIcon={isCreatingJob ? <HSpinner isButton /> : null}
                  fullWidth
                >
                  {isCreatingJob ? 'Creating Export Job...' : 'Create Export Job'}
                </HButton>

                {createJobError && (
                  <Alert severity='error' sx={{ mt: 2 }}>
                    Failed to create export job. Please check your configuration and try again.
                  </Alert>
                )}
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Export Jobs Management */}
      <Accordion defaultExpanded disableGutters elevation={0} style={{ width: '100%' }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{ background: theme => theme.palette.secondary.light }}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <JobIcon />
            <Typography variant='subtitle1'>Manage Export Jobs</Typography>
            {exportJobs.length > 0 && (
              <Box
                sx={{
                  backgroundColor: habuColors.primary[10],
                  color: habuColors.primary[60],
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {exportJobs.length}
              </Box>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {(() => {
            if (isLoadingJobs) {
              return (
                <Stack spacing={2}>
                  {[1, 2, 3].map((i) => (
                    <Card key={i} sx={{ p: 2 }} elevation={1}>
                      <Stack direction='row' alignItems='center' spacing={2}>
                        <Skeleton variant='rectangular' width={24} height={24} />
                        <Skeleton variant='text' width='60%' height={24} />
                        <Box sx={{ flexGrow: 1 }} />
                        <Skeleton variant='rounded' width={44} height={24} />
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              );
            }

            if (jobsError) {
              return (
                <Alert severity='error'>
                  Failed to load export jobs. Please try again.
                  <Box sx={{ mt: 1 }}>
                    <HButton
                      variant='outlined'
                      size='small'
                      onClick={() => refetchJobs()}
                    >
                      Retry
                    </HButton>
                  </Box>
                </Alert>
              );
            }

            if (!exportJobs.length) {
              return (
                <Stack spacing={3} alignItems='center' sx={{ textAlign: 'center', py: 4 }}>
                  <JobIcon sx={{ fontSize: 48, color: habuColors.neutral[40] }} />
                  <Typography variant='h6' color='text.secondary' gutterBottom>
                    No Export Jobs Created
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Export jobs created above will appear here for management.
                    You can enable or disable jobs to control their execution.
                  </Typography>
                </Stack>
              );
            }

            return (
              <Stack spacing={3}>
                <Typography variant='body3' color='text.secondary'>
                  Manage existing export jobs. Toggle jobs on/off to control their execution.
                  Active jobs will run on schedule, paused jobs will not execute until re-enabled.
                </Typography>

                <Grid container spacing={2}>
                  {exportJobs.map((job) => {
                    const isActive = job.status === 'ACTIVE';

                    return (
                      <Grid item xs={12} key={job.ID}>
                        <Card
                          sx={{
                            p: 2,
                            transition: 'all 0.2s ease',
                            border: `1px solid ${habuColors.neutral[20]}`,
                            '&:hover': {
                              borderColor: habuColors.primary[30],
                              boxShadow: `0 2px 8px ${habuColors.primary[10]}`,
                            },
                          }}
                          elevation={0}
                        >
                          <Stack direction='row' alignItems='center' spacing={2}>
                            {/* Job Icon and Status Indicator */}
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                backgroundColor: getStatusColor(job.status),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <JobIcon
                                sx={{
                                  fontSize: 20,
                                  color: isActive
                                    ? habuColors.signal.success[70]
                                    : habuColors.signal.warning[70],
                                }}
                              />
                            </Box>

                            {/* Job Details */}
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography
                                variant='subtitle2'
                                sx={{
                                  fontWeight: 500,
                                  color: habuColors.neutral[90],
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {job.name}
                              </Typography>

                              <Stack direction='row' alignItems='center' spacing={1}>
                                <Box
                                  sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    backgroundColor: getStatusColor(job.status),
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: isActive
                                      ? habuColors.signal.success[70]
                                      : habuColors.signal.warning[70],
                                  }}
                                >
                                  {getStatusText(job.status)}
                                </Box>

                                {job.partnerAccountName && (
                                <>
                                  <Typography variant='caption' color='text.secondary'>
                                    •
                                  </Typography>
                                  <Typography variant='caption' color='text.secondary'>
                                    {job.partnerAccountName}
                                  </Typography>
                                </>
                                )}
                              </Stack>
                            </Box>

                            {/* Status Toggle */}
                            <Box>
                              <FormControlLabel
                                control={(
                                  <Switch
                                    checked={isActive}
                                    onChange={() => handleStatusToggle(job.ID, job.status)}
                                    disabled={updatingJobs.has(job.ID)}
                                    size='small'
                                    sx={{
                                      '& .MuiSwitch-thumb': {
                                        backgroundColor: isActive
                                          ? habuColors.signal.success[40]
                                          : habuColors.neutral[50],
                                      },
                                      '& .MuiSwitch-track': {
                                        backgroundColor: isActive
                                          ? habuColors.signal.success[20]
                                          : habuColors.neutral[30],
                                      },
                                    }}
                                  />
                              )}
                                label=''
                                sx={{ m: 0 }}
                              />
                            </Box>
                          </Stack>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Summary */}
                <Box sx={{ pt: 2, borderTop: `1px solid ${habuColors.neutral[20]}` }}>
                  <Typography variant='caption' color='text.secondary'>
                    Total:
                    {' '}
                    {exportJobs.length}
                    {' '}
                    • Active:
                    {' '}
                    {exportJobs.filter(j => j.status === 'ACTIVE').length}
                    {' '}
                    •
                    Paused:
                    {' '}
                    {exportJobs.filter(j => j.status === 'PAUSED').length}
                  </Typography>
                </Box>
              </Stack>
            );
          })()}
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default FlowNodeExportConfiguration;
